<script setup lang="ts">
import CurrencyText from '@uiKit/components/CurrencyText.vue'
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import { InfoIcon } from '@uiKit/icons'
import Text from '@uiKit/elements/Text.vue'
import CopyableDetailRow from './CopyableDetailRow.vue'
import type { SlicealAccount } from '../types/fiatWalletTypes'

interface Props {
  /** Pre-formatted deposit amount, e.g. `"5,000"`. */
  amount: string
  currency?: string
  slicealAccount?: SlicealAccount | null
}

const props = withDefaults(defineProps<Props>(), {
  currency: 'AED',
  slicealAccount: null,
})

const PLACEHOLDER = '-'

const rows = computed(() => {
  const account = props.slicealAccount
  return [
    { label: 'Account Name', value: account?.accountName || PLACEHOLDER },
    { label: 'Bank Name', value: account?.bankName || PLACEHOLDER },
    {
      label: 'IBAN',
      value: account?.iban || PLACEHOLDER,
      // Shown in spaced groups for transcription, copied unspaced so it pastes
      // straight into a banking app's IBAN field.
      copyValue: (account?.iban ?? '').replace(/\s/g, ''),
    },
    { label: 'SWIFT / BIC', value: account?.swiftBic || PLACEHOLDER },
  ]
})

const steps = computed(() => [
  'Copy the bank details above and open your banking app.',
  `Initiate a transfer for exactly ${props.currency} ${props.amount}.`,
  'Return here and tap I Have Made the Transfer.',
])
</script>

<template>
  <Box direction="col" gap="6" class="dbt">
    <Box direction="col" gap="1">
      <Text type="body" weight="bold" color="muted" transform="uppercase" is="span">
        Deposit amount
      </Text>
      <Text type="display" weight="semibold" is="p" class="dbt__amount">
        <CurrencyText :text="`${currency} ${amount}`" />
      </Text>
    </Box>

    <Box direction="col" gap="3" is="section" class="dbt__account">
      <Text type="body" weight="bold" color="muted" is="h3">Sliceal Bank Account</Text>

      <Box direction="col" class="dbt__rows">
        <CopyableDetailRow
          v-for="row in rows"
          :key="row.label"
          :label="row.label"
          :value="row.value"
          :copy-value="row.copyValue"
          class="dbt__row"
        />
      </Box>
    </Box>

    <Text type="body" weight="bold" color="muted" transform="uppercase" is="h3">
      How to complete your transfer
    </Text>

    <Box direction="col" gap="3" is="ol" class="dbt__steps">
      <Box
        v-for="(step, index) in steps"
        :key="step"
        is="li"
        align="start"
        gap="3"
        class="dbt__step"
      >
        <Box flex align="center" justify="center" class="dbt__step-num">
          <Text type="body" weight="semibold" is="span">{{ index + 1 }}</Text>
        </Box>
        <Text type="heading" weight="regular" color="muted" is="span" class="dbt__step-text">
          {{ step }}
        </Text>
      </Box>
    </Box>

    <Box align="start" gap="3" class="dbt__note">
      <InfoIcon class="dbt__note-icon" />
      <Text type="body" color="muted" is="p">
        Please make the transfer from your banking app and include any reference details provided by
        your bank. Funds typically reflect in your wallet within 1–2 working days.
      </Text>
    </Box>
  </Box>
</template>

<style scoped>
.dbt__amount {
  font-variant-numeric: tabular-nums;
}

.dbt__account {
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: 12px;
  padding: 1.125rem 1.25rem;
  box-sizing: border-box;
}

.dbt__rows {
  width: 100%;
}

.dbt__row:not(:last-child) {
  border-bottom: 1px solid var(--p-content-border-color);
}

.dbt__steps {
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
}

.dbt__step-num {
  width: 26px;
  height: 26px;
  border-radius: 40px;
  background: var(--p-surface-100);
  flex-shrink: 0;
}

.dbt__step-text {
  min-width: 0;
  line-height: 1.45;
}

.dbt__note {
  background: var(--p-surface-100);
  border-radius: 12px;
  padding: 0.875rem 1.125rem;
}

.dbt__note-icon {
  font-size: 18px;
  color: var(--p-text-muted-color);
  flex-shrink: 0;
  margin-top: 2px;
}
</style>
